import React, { useState, useEffect } from 'react';
import { FaDollarSign, FaBox } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { showToast } from '../../utils/toast';
import { postApi } from '../../utils/api/api';
import DeleteImages from '../Common/DeleteImages';

const AddInventoryForm = ({ selectedOption }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    material: '',
    qty: '',
    qty_type: '',
    price: '',
    dying: '',
    count_to: '',
    count_from: '',
    avg_csp_from: '',
    avg_csp_to: '',
    images: [],
    certificate: null,
    category: '',
    sub_category: '',
    end_use: '',
    technology: '',
    ply: '',
    application: '',
    yarn_speciality: '',
    ipi: '',
    quality_grade: '',
    spinning_process: '',
    spinning_type: '',
    blend1: '',
    blend1_percentage: '',
    blend2: '',
    blend2_percentage: '',
    // price: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showBlendFields, setShowBlendFields] = useState(false);

  // Options for dropdowns
  const materialsOptions = ["Cotton 100%", "PES 100%", 'TC PES 60% Cotton 40%', 'TC PES 52% Cotton 48%', 'CVC Cotton 60% PES 40%',
    'CVC Cotton 52% PES 48%', 'TR PES 60% Rayon 40%', 'TR PES 65% Rayon 35%', 'Rayon 100% Raw', 'Rayon 100% White',
    'LWA Eco', 'Modal', 'Bamboo', 'Blends Specify', 'Filament DTY', 'Filament FDY', 'Filament SDY', 'Nylon', 'Misty/Melange M61', 'Misty/Melange M71', 'Misty/Melange M81',
    'Misty/Melange 100% Black', 'Sewing Threads', 'Specify'];
  const spinningTypeOptions = ['Carded', 'Combed', 'Compact', 'Non compact', 'Semi-dull', 'Bright', 'Super Bright', 'High Tenacity'];
  const spinningProcessOptions = ["Open end", "Ring", "MVS", "Texturizing"];
  const yarnUsageOptions = ['Knitting yarn', 'Weaving yarn WEFT (PAKAN)', 'Weaving yarn WARP (LUSI)'];
  const qualityGradeOptions = ["A", "B"];
  const dyingOptions = ["Print quality", "Dying quality", 'Both'];
  const specialYarnsOptions = ['Filament DTY', 'Filament FDY', 'Filament SDY', 'Nylon', 'Misty/Melange M61', 'Misty/Melange M71', 'Misty/Melange M81',
    'Misty/Melange 100% Black', 'Sewing Threads', 'Specify'];
  const quantityTypeOptions = ['Kg', 'Bundle'];
  const categoryOptions = ['Cotton','Polyester','Viscose','Linen'];
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const endUseOptions = ['Weaving','Knitting/Hosiery','Sewing Thread','Embroidery','Filtration'];
  const technologyOptions = ['Ring','Open End','Vortex','Airjet','Wet','Friction'];
  const plyOptions = ['Single', 'Double', 'Multi-Fold'];
  const applicationOptions = ['Warp','Weft','Warp&Weft','Circular','Filter'];
  const yarnSpecialityOptions = ['Slub','High-twist','Organic','BCI','Neps','Siro/ Elli Twist','Injected Slub','Super High Twist','TFO'];
  const blendOptions = ["Cotton Carded", "Cotton Combed", "Giza", "Supima", "Pima", "BCI Cotton", "Polyester", "Micro Polyester", 
    "Recycled Polyester", "Viscose", "Micro Viscose", "Liva Eco", "Ecovera", "Modal", "Micro Modal", "Excel", "Micro Excel", 
    "Tencel", "Micro Tencel", "Acrylic", "Linen", "Remie", "Hemp", "Bamboo", "Banana", "Soya", "Nylon", "Cupro", "Silk"];

  // Update sub-category options based on selected category
  useEffect(() => {
    if (formData.category === 'Cotton') {
      setSubCategoryOptions(['Carded', 'Combed', 'Combed Compact', 'BCI', 'Organic', 'Giza', 'Giza Blend', 'Supima', 'Pima']);
      setFormData(prev => ({ ...prev, sub_category: '' }));
    } else if (formData.category === 'Polyester') {
      setSubCategoryOptions(['Micro', 'Recycled', 'Lycra','Semi Dull','Bright','High Tenacity']);
      setFormData(prev => ({ ...prev, sub_category: '' }));
    } else if (formData.category === 'Viscose') {
      setSubCategoryOptions(['Micro', 'Liva Eco', 'Ecovera', 'Modal', 'Micro Modal', 'Excel', 'Micro Excel', 'Tencel', 'Micro Tencel', 'Bamboo', 'Lycra','Semi Dull','Bright','High Tenacity']);
      setFormData(prev => ({ ...prev, sub_category: '' }));
    } else if (formData.category === 'Linen') {
      setSubCategoryOptions(['Long Fiber', 'Short Fiber']);
      setFormData(prev => ({ ...prev, sub_category: '' }));
    } else {
      setSubCategoryOptions([]);
      setFormData(prev => ({ ...prev, sub_category: '' }));
    }
  }, [formData.category]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file && field === 'certificate' && !file.type.includes('pdf')) {
      setErrors((prev) => ({ ...prev, [field]: 'Please upload a PDF file only.' }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: file }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(file => !['image/jpeg', 'image/png'].includes(file.type));

    if (invalidFiles.length > 0) {
      setErrors((prev) => ({ ...prev, images: 'Please upload only JPG or PNG files.' }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, ...files],
    }));
    if (files.length > 0) {
      setErrors((prev) => ({ ...prev, images: '' }));
    }
  };

  const handleImageDelete = (index) => {
    setFormData((prev) => {
      const updatedImages = prev.images.filter((_, i) => i !== index);
      return { ...prev, images: updatedImages };
    });
  };

  const handleCertificateDelete = () => {
    setFormData((prev) => ({ ...prev, certificate: null }));
  };

  const toggleBlendFields = () => {
    setShowBlendFields(!showBlendFields);
    // Reset blend fields when toggling off
    if (showBlendFields) {
      setFormData(prev => ({
        ...prev,
        blend1: '',
        blend1_percentage: '',
        blend2: '',
        blend2_percentage: ''
      }));
    }
  };

  const validateForm = () => {
    const { name, material, qty, qty_type,  description, images, certificate, spinning_process, spinning_type, quality_grade, count_from, count_to, blend1, blend1_percentage, blend2, blend2_percentage } = formData;
    const newErrors = {};

    // if (!material?.trim()) newErrors.material = t('inventoryErrors.materialRequired', 'Material is required.');
    
    // if (!qty || isNaN(qty) || qty <= 0) newErrors.qty = t('inventoryErrors.qtyInvalid', 'Enter a valid quantity.');
    // if (!qty_type?.trim()) newErrors.qty_type = t('inventoryErrors.qtyTypeRequired', 'Quantity type is required.');
    // if (!count_from || isNaN(count_from)) newErrors.count_from = t('inventoryErrors.countFromRequired', 'Count from is required.');
    // if (!count_to || isNaN(count_to)) newErrors.count_to = t('inventoryErrors.countToRequired', 'Count to is required.');
    // if (parseFloat(count_from) > parseFloat(count_to)) newErrors.count_to = t('inventoryErrors.countToInvalid', 'Count To must be greater than or equal to Count From.');
    // if (!price || isNaN(price) || price <= 0) newErrors.price = t('inventoryErrors.priceInvalid', 'Enter a valid price.');
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.sub_category?.trim()) newErrors.sub_category = 'Sub Category is required';
    if (!quality_grade?.trim()) newErrors.quality_grade = t('inventoryErrors.qualityGradeRequired', 'Quality grade is required.');
    // if (!price?.trim()) newErrors.price =  'Price is required.';    
    // if (images.length === 0) newErrors.images = t('inventoryErrors.imageRequired', 'Please upload at least one image.');
    // if (!certificate) newErrors.certificate = t('inventoryErrors.certificateRequired', 'Please upload a certificate.');

    // Validate blend fields if shown
    if (showBlendFields) {
      if (!blend1?.trim()) newErrors.blend1 = 'Blend 1 is required';
      if (!blend1_percentage?.trim() || isNaN(blend1_percentage)) newErrors.blend1_percentage = 'Enter a valid percentage';
      if (!blend2?.trim()) newErrors.blend2 = 'Blend 2 is required';
      if (!blend2_percentage?.trim() || isNaN(blend2_percentage)) newErrors.blend2_percentage = 'Enter a valid percentage';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      // console.log("Validation Errors:", errors);
      showToast('error', t('errors.validationFailed', 'Please correct the errors in the form.'));
      return;
    };
    setLoading(true);
    try {
      const payload = { 
        ...formData, 
        price_type: selectedOption,
        form_type: 'Spun' // Adding category type as 'yarn' for this form
      };

      const formDataPayload = new FormData();
      Object.keys(payload).forEach((key) => {
        if (key === 'images') {
          payload.images.forEach((image) => formDataPayload.append('images[]', image));
        } else if (key === 'certificate') {
          formDataPayload.append('certificate', payload.certificate);
        } else {
          formDataPayload.append(key, payload[key]);
        }
      });

      await postApi('/seller/create-product', formDataPayload);
      showToast('success', t('success.inventoryAdded', 'Inventory added successfully!'));
      navigate('/seller-dashboard');
    } catch (err) {
      showToast('error', err.message || t('errors.submitFailed', 'Failed to submit the form.'));
    } finally {
      setLoading(false);
    }
  };

  // Helper function to render form fields
  const renderField = (label, name, type = 'text', options = [], required = false, placeholder = '') => (
    <div className="form-group">
      <label htmlFor={name}>
        {label} {required && <span className="text-danger">*</span>}
      </label>
      {type === 'select' ? (
        <select
          id={name}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className={`form-control form-select ${errors[name] ? 'is-invalid' : ''}`}
        >
          <option value="">{`Select ${label}`}</option>
          {options.map((option, index) => (
            <option key={index} value={option}>{option}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea
          id={name}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          maxLength="500"
          className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
          rows="4"
          placeholder={placeholder}
        ></textarea>
      ) : (
        <div className="input-with-icon">
          <input
            type={type}
            id={name}
            name={name}
            value={formData[name]}
            onChange={handleInputChange}
            className={`form-control ${errors[name] ? 'is-invalid' : ''}`}
            placeholder={placeholder || label}
          />
          {/*{(name === 'price' ) && (
            <span className="input-icon">
              {selectedOption === "$ / kg" ? (
                <FaDollarSign size={20} color="green" />
              ) : (
                <FaBox size={20} color="blue" />
              )}
            </span>
          )}*/}
        </div>
      )}
      {errors[name] && <div className="invalid-feedback">{errors[name]}</div>}
    </div>
  );

  return (
    <div className="add-inventory-container common-form-container">
      <div className="form-card">
        <div className="form-body">
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              {renderField('Category', 'category', 'select', categoryOptions, true)}
              {renderField('Sub-Category', 'sub_category', 'select', subCategoryOptions,true)}
              
              {renderField('End Use', 'end_use', 'select', endUseOptions)}
              {renderField('Technology', 'technology', 'select', technologyOptions)}
              {renderField('Count From', 'count_from', 'number', [], false, 'e.g., 20')}
              {renderField('Count To', 'count_to', 'number', [], false, 'e.g., 40')}
              {renderField('Avg CSP From', 'avg_csp_from', 'number', [], false, 'e.g., 2000')}
              {renderField('Avg CSP To', 'avg_csp_to', 'number', [], false, 'e.g., 2500')}
              {renderField('Ply', 'ply', 'select', plyOptions)}
              {renderField('Application', 'application', 'select', applicationOptions)}
              {renderField('Yarn Speciality', 'yarn_speciality', 'select', yarnSpecialityOptions)}
              {/*{renderField('Price', 'price', 'number', [], false, 'e.g., 2500')}*/}
              {/* Quality Parameters Section */}
              <div className="full-width">
                <div className='d-flex justify-content-between form-head-wrap'>
                  <div className='d-flex form-check-wrap'>
                    <label className="form-check-label" htmlFor="flexSwitchCheckDefault" style={{ marginRight: '27px' }}> Single</label>
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="flexSwitchCheckDefault"
                        checked={showBlendFields}
                        onChange={toggleBlendFields}
                      />
                      <label className="form-check-label" htmlFor="flexSwitchCheckDefault">
                        Blend
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              {showBlendFields && (
                <>
                <div className='full-width row'>
                 <div className='col-lg-6'> {renderField('Blend 1', 'blend1', 'select', blendOptions, true)} </div>
                 <div className='col-lg-6'>{renderField('Blend 1 %', 'blend1_percentage', 'number', [], true, 'e.g., 60')}</div>
                 <div className='col-lg-6'>{renderField('Blend 2', 'blend2', 'select', blendOptions, true)}</div>
                 <div className='col-lg-6'>{renderField('Blend 2 %', 'blend2_percentage', 'number', [], true, 'e.g., 40')}</div>
                  </div>
                </>
              )}

              {/* Product Images and Certificate */}
              <div className='card p-2 full-width' id="commonFields">
              <div className='row'>
                <div className='col-lg-6'>
              {renderField('IPI', 'ipi', 'text', [], false, 'e.g., 50')}
              </div>
              <div className='col-lg-6'>
              {renderField('Quality Grade', 'quality_grade', 'select', qualityGradeOptions, true)}
              </div>
              <div className='col-lg-6'>
              <div className="form-group full-width">
                <label htmlFor="images">
                  Product Images  <span className="badge">JPG, PNG only</span>
                </label>
                <input
                  type="file"
                  id="images"
                  multiple
                  accept="image/jpeg, image/png"
                  onChange={handleImageChange}
                  className={`form-control ${errors.images ? 'is-invalid' : ''}`}
                />
                <DeleteImages images={formData.images} onDelete={handleImageDelete} />
                {errors.images && <div className="invalid-feedback d-block">{errors.images}</div>}
              </div>
              </div>
              <div className='col-lg-6'>
              <div className="form-group full-width">
                <label htmlFor="certificate">
                  Upload Certificate <span className="badge">PDF only</span>
                </label>
                <input
                  type="file"
                  id="certificate"
                  accept="application/pdf"
                  onChange={(e) => handleFileChange(e, 'certificate')}
                  className={`form-control ${errors.certificate ? 'is-invalid' : ''}`}
                />
                {formData.certificate && (
                  <div className="certificate-preview">
                    <span>{formData.certificate.name}</span>
                    <button type="button" className="btn-delete-certificate" onClick={handleCertificateDelete}>
                      &times;
                    </button>
                  </div>
                )}
                {errors.certificate && <div className="invalid-feedback d-block">{errors.certificate}</div>}
              </div>
              </div>
              </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Submitting...
                  </>
                ) : (
                  'Add Inventory'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddInventoryForm;